import {
  useState
} from "react";
import {
  changePassword
} from "../api/passwordApi";
import {
  KeyRound,
  Eye,
  EyeOff,
  ArrowLeft,
  Check,
  ShieldCheck,
  Lock
} from "lucide-react";

import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Input
} from "../components/ui";
import {
  useToast
} from "../context/ToastContext";

const ChangePassword = () => {
  // Functional State Values
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const toast = useToast();

  // UI Interactive States
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSuccessStep, setIsSuccessStep] = useState(false);

  // Requirement Validation Metrics
  const hasLength = newPassword.length >= 8;
  const hasComplexity = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{1,}$/.test(newPassword);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword !== "";
  const isSubmitDisabled = !(hasLength && hasComplexity && passwordsMatch && currentPassword) || loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitDisabled) return;

    try {
      setLoading(true);
      setMessage("");

      await changePassword(currentPassword, newPassword);

      setMessage("Your account security has been updated. You can now use your new password to access the EduClarify ERP portal.");
      setIsSuccessStep(true);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to update your password. Please verify your current credentials and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResetForm = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setMessage("");
    setIsSuccessStep(false);
  };

  return (
    <div className="min-h-screen bg-neutral-50/50 flex flex-col items-center justify-center p-4 sm:p-6 lg:p-8 antialiased">
      <div className="w-full max-w-md space-y-5">
        
        {/* Logo & Header */}
        <div className="text-center space-y-2">
          <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary-50 text-primary-600 dark:text-primary-400 shadow-xs">
            <KeyRound className="h-5 w-5" />
          </div>
          <h1 className="text-xl font-bold tracking-tight text-neutral-900 dark:text-neutral-100">
           Change Your Password
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
            Update your account password to ensure platform security
          </p>
        </div>

        {/* Main Card Container */}
        <Card className="border border-neutral-200 dark:border-neutral-800 shadow-none">
          <CardHeader className="border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-900/50 px-5 py-3.5">
            <CardTitle className="text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider flex items-center gap-1.5">
              <Lock className="h-3.5 w-3.5 text-neutral-400 dark:text-neutral-500" />
              Password & Security
            </CardTitle>
          </CardHeader>

          <CardContent className="p-5">
            {!isSuccessStep ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                
                {/* Global Error Notice Display */}
                

                {/* Current Password Field Block */}
                <div className="relative">
                  <Input
                    label="Current Password"
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-[34px] text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 transition-colors"
                  >
                    {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>

                {/* New Password Field Block */}
                <div className="relative">
                  <Input
                    label="New Password"
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-[34px] text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 transition-colors"
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>

                {/* Confirm New Password Field Block */}
                <div className="relative">
                  <Input
                    label="Confirm New Password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-[34px] text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>

                {/* Security Requirements Checklist Card */}
                <div className="rounded-lg border border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 p-3 space-y-2">
                  <p className="text-[11px] font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Password Requirements
                  </p>
                  <div className="space-y-1.5 text-xs">
                    <div className="flex items-center gap-2">
                      <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] ${hasLength ? "bg-success-100 text-success-700" : "bg-neutral-200 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400"}`}>
                        {hasLength ? <Check className="h-2.5 w-2.5" /> : "•"}
                      </span>
                      <span className={hasLength ? "text-neutral-800 dark:text-neutral-100 font-medium" : "text-neutral-400 dark:text-neutral-500"}>
                        At least 8 characters long
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] ${hasComplexity ? "bg-success-100 text-success-700" : "bg-neutral-200 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400"}`}>
                        {hasComplexity ? <Check className="h-2.5 w-2.5" /> : "•"}
                      </span>
                      <span className={hasComplexity ? "text-neutral-800 dark:text-neutral-100 font-medium" : "text-neutral-400 dark:text-neutral-500"}>
                        Letters, numbers & special characters
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] ${passwordsMatch ? "bg-success-100 text-success-700" : "bg-neutral-200 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400"}`}>
                        {passwordsMatch ? <Check className="h-2.5 w-2.5" /> : "•"}
                      </span>
                      <span className={passwordsMatch ? "text-neutral-800 dark:text-neutral-100 font-medium" : "text-neutral-400 dark:text-neutral-500"}>
                        Passwords match
                      </span>
                    </div>
                  </div>
                </div>

                {/* Submit Action Button */}
                <Button
                  type="submit"
                  variant="primary"
                  isLoading={loading}
                  disabled={isSubmitDisabled}
                  className="w-full text-xs font-semibold py-2.5 mt-2"
                >
                  Reset Password
                </Button>

                {/* Back to Login Anchor */}
                <div className="text-center pt-2">
                  <a
                    href="#/login"
                    className="inline-flex items-center gap-1 text-xs text-neutral-500 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-100 font-medium transition-colors"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" /> Back to Login
                  </a>
                </div>
              </form>
            ) : (
              /* Success View */
              <div className="text-center py-4 space-y-4">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-success-50 text-success-600">
                  <ShieldCheck className="h-6 w-6" />
                </div>
                <div className="space-y-1">
                  <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
                    Password Reset Successful
                  </h2>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed px-2">
                    {message}
                  </p>
                </div>
                
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleResetForm}
                  className="w-full text-xs font-semibold py-2.5"
                >
                  Go to Dashboard
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer info */}
        <p className="text-center text-[11px] text-neutral-400 dark:text-neutral-500">
          © 2026 EduClarify ERP. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default ChangePassword;